import React from "react";
import { useSelector } from "react-redux";
import { Route, Switch } from "react-router-dom";

import { idOrIdentifierSchema } from "~/tools/id-or-identifier";

import { showWelcomePage } from "../../app/state/config/ConfigSelectors";
import { DashboardBreadcrumb } from "../dashboards/dashboard/breadcrumb";
import {
	DASHBOARDS_OVERVIEW_PATH,
	DASHBOARD_PATH,
	KUBERNETES_OVERVIEW_PATH,
	MONITORS_PATH,
	NOTIFICATIONS_PATH,
	SETTINGS_PATH,
	TRACES_PATH,
	VIEWS_OVERVIEW_PATH,
} from "../Routes";

import { AnalyticsIndexBreadcrumb } from "./analytics/AnalyticsIndexBreadcrumb";
import { CLIProfileBreadcrumb } from "./cli/CLIProfileBreadcrumb";
import { DashboardsOverviewBreadcrumb } from "./dashboards/dashboards-overview-breadcrumb";
import { MetricsBreadcrumb } from "./metrics/MetricsBreadcrumb";
import { TracesBreadcrumb } from "./monitors/TracesBreadcrumb";
import { StackPacksDetailBreadcrumb } from "./stackPacks/StackPacksDetailBreadcrumb";
import { StackPacksIndexBreadcrumb } from "./stackPacks/StackPacksIndexBreadcrumb";
import { StackPacksIntegrationDetailBreadcrumb } from "./stackPacks/StackPacksIntegrationDetailBreadcrumb";
import { StackPacksTypeBreadcrumb } from "./stackPacks/StackPacksTypeBreadcrumb";
import { ComponentNameBreadcrumb } from "./views/ComponentNameBreadcrumb";
import { ComponentsIndexBreadcrumb } from "./views/ComponentsIndexBreadcrumb";
import { KubernetesOverviewBreadcrumb } from "./views/KubernetesOverviewBreadcrumb";
import { ViewNameBreadcrumb } from "./views/ViewNameBreadcrumb";
import { ViewsIndexBreadcrumb } from "./views/ViewsIndexBreadcrumb";
import { HomeBreadcrumb } from "./welcome/HomeBreadcrumb";
import { NotificationsBreadcrumb } from "./_notifications-breadcrumb";
import { MonitorsBreadcrumb } from "./monitors";

const LazySettingsRootBreadcrumbs = React.lazy(() =>
	import("./_settings-breadcrumb").then(({ SettingsRootBreadcrumbs }) => ({
		default: SettingsRootBreadcrumbs,
	})),
);

export interface BreadcrumbsProps {
	isRightBorderFlat?: boolean;
}

export const Breadcrumbs: React.FC<BreadcrumbsProps> = ({ isRightBorderFlat }) => {
	const welcomePageEnabled = useSelector(showWelcomePage);
	return (
		<div
			className="flex min-w-content items-center gap-px"
			data-testid="breadcrumbs"
			data-tourid="breadcrumbs"
			id="breadCrumb"
		>
			{welcomePageEnabled && <HomeBreadcrumb />}

			<Switch>
				<Route path={VIEWS_OVERVIEW_PATH}>
					<ViewsIndexBreadcrumb />
					<Route component={ViewNameBreadcrumb} path={ViewNameBreadcrumb.PATH} />
				</Route>

				<Route path={ComponentsIndexBreadcrumb.PATH}>
					<ComponentsIndexBreadcrumb />
					<Route component={ComponentNameBreadcrumb} path={ComponentNameBreadcrumb.PATH} />
				</Route>
			</Switch>

			<Route path={DASHBOARDS_OVERVIEW_PATH}>
				<DashboardsOverviewBreadcrumb />
				<Route
					path={DASHBOARD_PATH}
					render={({ match: { params } }) => (
						<DashboardBreadcrumb idOrIdentifier={idOrIdentifierSchema.parse(params.idOrIdentifier)} />
					)}
				/>
			</Route>

			<Route component={KubernetesOverviewBreadcrumb} path={KUBERNETES_OVERVIEW_PATH} />

			<Route component={NotificationsBreadcrumb} path={NOTIFICATIONS_PATH} />

			<Route component={StackPacksIndexBreadcrumb} path={StackPacksIndexBreadcrumb.PATH} />
			<Route component={StackPacksTypeBreadcrumb} path={StackPacksTypeBreadcrumb.PATH} />
			<Route component={StackPacksDetailBreadcrumb} path={StackPacksDetailBreadcrumb.PATH} />
			<Route component={StackPacksIntegrationDetailBreadcrumb} path={StackPacksIntegrationDetailBreadcrumb.PATH} />

			<Route component={AnalyticsIndexBreadcrumb} path={AnalyticsIndexBreadcrumb.PATH} />

			<Route component={CLIProfileBreadcrumb} path={CLIProfileBreadcrumb.PATH} />

			<Route component={MonitorsBreadcrumb} path={MONITORS_PATH} />

			<Route component={MetricsBreadcrumb} path={MetricsBreadcrumb.PATH} />

			<Route component={TracesBreadcrumb} path={TRACES_PATH} />

			<Route path={SETTINGS_PATH}>
				<React.Suspense fallback={null}>
					<LazySettingsRootBreadcrumbs />
				</React.Suspense>
			</Route>

			{isRightBorderFlat && (
				<span className="-ml-px">{/* the dummy component forces the last breadcrumb's right border to be flat */}</span>
			)}
		</div>
	);
};
